import { useContext, useMemo } from 'react';
import { ObjectTypeId } from 'customer-data-objects/constants/ObjectTypeIds';
import { HighlySensitivePropertyValues } from './useHighlySensitivePropertyValues';
import { HighlySensitivePropertyContext } from './HighlySensitivePropertyContext';

type AllHighlySensitivePropertyValues = {
  [objectId: string]: HighlySensitivePropertyValues;
};

export const useAllHighlySensitivePropertyValues = ({
  objectTypeId,
  defaultValues,
}: {
  objectTypeId: ObjectTypeId;
  defaultValues?: AllHighlySensitivePropertyValues;
}): AllHighlySensitivePropertyValues => {
  const context = useContext(HighlySensitivePropertyContext);
  if (!context && !defaultValues) {
    throw new Error(
      'useHighlySensitivePropertyValues must be used within a HighlySensitivePropertyContextProvider'
    );
  }

  return useMemo(() => {
    if (!context) {
      return defaultValues!;
    }
    return Array.from(context.propertyValuesMap.values())
      .filter(
        (propertyValue) =>
          propertyValue.objectTypeId === objectTypeId &&
          propertyValue.status === 'SUCCEEDED' &&
          propertyValue.timestamp === undefined
      )
      .reduce<AllHighlySensitivePropertyValues>((acc, propertyValue) => {
        acc[propertyValue.objectId] ||= {};
        acc[propertyValue.objectId][propertyValue.propertyName] =
          propertyValue.value;
        return acc;
      }, {});
  }, [context, defaultValues, objectTypeId]);
};
